import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Heart, MapPin, DollarSign, Award, Languages, Shield, ArrowLeft, Star, X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVIEW_TAGS = [
  "Compassionate", "Knowledgeable", "Great Listener", "Professional",
  "Culturally Aware", "Healing Touch", "Warm & Welcoming",
  "Life Changing", "Highly Skilled", "Patient"
];

const HealerProfile = () => {
  const { healerId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [healer, setHealer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reviews
  const [reviews, setReviews] = useState([]);
  const [userBookings, setUserBookings] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewTags, setReviewTags] = useState([]);
  const [reviewBookingId, setReviewBookingId] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  // Contact message
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageName, setMessageName] = useState('');
  const [messageEmail, setMessageEmail] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Gallery lightbox
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const galleryPhotos = healer?.photos || [];
  const galleryUrl = (p) => (p.startsWith('http') ? p : `${API}/files/${p}`);
  const closeLightbox = () => setLightboxIndex(null);
  const prevLightbox = () => setLightboxIndex((i) => (i === 0 ? galleryPhotos.length - 1 : i - 1));
  const nextLightbox = () => setLightboxIndex((i) => (i === galleryPhotos.length - 1 ? 0 : i + 1));

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') prevLightbox();
      else if (e.key === 'ArrowRight') nextLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, galleryPhotos.length]);

  useEffect(() => {
    fetchHealerProfile();
    fetchReviews();
  }, [healerId]);

  useEffect(() => {
    if (user && token) {
      fetchUserBookings();
    }
  }, [user, healerId]);

  const fetchHealerProfile = async () => {
    try {
      const response = await axios.get(`${API}/healers/${healerId}`);
      setHealer(response.data);
    } catch (error) {
      toast.error('Failed to load healer profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/healer/${healerId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchUserBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only bookings with this healer that are confirmed/completed
      const healerBookings = response.data.filter(
        b => b.healer_id === healerId && ['confirmed', 'completed'].includes(b.status)
      );
      setUserBookings(healerBookings);
    } catch (error) {
      console.error('Error fetching user bookings:', error);
    }
  };

  const toggleTag = (tag) => {
    setReviewTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmitReview = async () => {
    if (!reviewBookingId) {
      toast.error('Please select a booking to review');
      return;
    }
    if (!reviewText.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        healer_id: healerId,
        booking_id: reviewBookingId,
        rating: reviewRating,
        text: reviewText,
        tags: reviewTags
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Review submitted!');
      setShowReviewForm(false);
      setReviewRating(5);
      setReviewText('');
      setReviewTags([]);
      setReviewBookingId('');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Check which bookings haven't been reviewed yet
  const reviewableBookings = userBookings.filter(
    b => !reviews.some(r => r.booking_id === b.id)
  );

  const handleSendMessage = async () => {
    if (!messageName.trim() || !messageEmail.trim() || !messageText.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setSendingMessage(true);
    try {
      await axios.post(`${API}/healers/${healerId}/message`, {
        sender_name: messageName,
        sender_email: messageEmail,
        message: messageText
      });
      toast.success('Message sent! The healer will receive your message via email.');
      setShowMessageForm(false);
      setMessageName('');
      setMessageEmail('');
      setMessageText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!healer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Healer not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Helmet>
        <title>{healer?.user?.name ? `${healer.user.name} - ${healer.healer_type}` : 'Healer Profile'} | Ourmend</title>
        <meta name="description" content={`Book a session with ${healer?.user?.name || 'this healer'}, a ${healer?.healer_type || 'healer'} on Ourmend. ${healer?.bio?.slice(0, 120) || ''}`} />
      </Helmet>
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Heart className="w-8 h-8 text-#8B4513" />
            <span className="text-2xl font-bold text-gray-900">Ourmend</span>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Section */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-8">
              <div className="flex items-start gap-6">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-teal-400 to-amber-500 flex-shrink-0 overflow-hidden">
                  {healer.profile_image && (
                    <img
                      src={healer.profile_image.startsWith('http') ? healer.profile_image : `${API}/files/${healer.profile_image}`}
                      alt={healer.user?.name}
                      className="w-full h-full object-cover"
                      data-testid="healer-profile-image"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <Badge className="mb-3">{healer.healer_type}</Badge>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">{healer.user?.name || 'Unknown'}</h1>
                  <div className="flex items-center gap-4 text-gray-600 mb-4">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span>{healer.location}</span>
                    </div>
                    <div className="flex items-center">
                      <Award className="w-4 h-4 mr-1" />
                      <span>{healer.years_experience} years</span>
                    </div>
                  </div>
                  <div className="flex items-center text-2xl font-bold" style={{color: '#8B4513'}}>
                    <DollarSign className="w-6 h-6" />
                    <span>{healer.hourly_rate}/hour</span>
                  </div>
                  {avgRating && (
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-5 h-5 ${s <= Math.round(avgRating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      <span className="font-semibold text-gray-700">{avgRating}</span>
                      <span className="text-sm text-gray-500">({reviews.length} review{reviews.length !== 1 ? 's' : ''})</span>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Practice Quote */}
            {healer.practice_quote && (
              <Card className="p-8 bg-amber-50/50 border-amber-200" data-testid="practice-quote">
                <div className="flex gap-4">
                  <span className="text-5xl font-serif text-amber-300 leading-none">"</span>
                  <p className="text-lg text-gray-800 italic leading-relaxed pt-2">{healer.practice_quote}</p>
                </div>
              </Card>
            )}

            <Card className="p-8">
              <h2 className="text-2xl font-bold mb-4">About</h2>
              <p className="text-gray-700 leading-relaxed">{healer.bio}</p>
            </Card>

            {/* Photo Gallery */}
            {galleryPhotos.length > 0 && (
              <Card className="p-8" data-testid="gallery-section">
                <div className="flex items-center gap-2 mb-4">
                  <ImageIcon className="w-6 h-6 text-amber-700" />
                  <h2 className="text-2xl font-bold">Gallery</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="gallery-grid">
                  {galleryPhotos.map((photoPath, idx) => (
                    <button
                      key={photoPath}
                      type="button"
                      onClick={() => setLightboxIndex(idx)}
                      className="relative aspect-square rounded-lg overflow-hidden border border-stone-200 group focus:outline-none focus:ring-2 focus:ring-amber-500"
                      data-testid={`gallery-thumb-${idx}`}
                      aria-label={`View photo ${idx + 1}`}
                    >
                      <img
                        src={galleryUrl(photoPath)}
                        alt={`${healer.user?.name || 'Healer'} gallery ${idx + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* Accolades */}
            {healer.accolades && (
              <Card className="p-8" data-testid="accolades-section">
                <h2 className="text-2xl font-bold mb-4">Accolades & Achievements</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{healer.accolades}</p>
              </Card>
            )}

            {healer.specialties && healer.specialties.length > 0 && (
              <Card className="p-8">
                <h2 className="text-2xl font-bold mb-4">Specialties</h2>
                <div className="flex flex-wrap gap-2">
                  {healer.specialties.map((specialty, idx) => (
                    <Badge key={idx} variant="outline" className="text-sm py-2 px-4">
                      {specialty}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {healer.languages && healer.languages.length > 0 && (
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Languages className="w-5 h-5 text-#8B4513" />
                    <h3 className="font-bold text-lg">Languages</h3>
                  </div>
                  <div className="space-y-2">
                    {healer.languages.map((lang, idx) => (
                      <p key={idx} className="text-gray-700">{lang}</p>
                    ))}
                  </div>
                </Card>
              )}

              {healer.insurance_accepted && healer.insurance_accepted.length > 0 && (
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-#8B4513" />
                    <h3 className="font-bold text-lg">Insurance</h3>
                  </div>
                  <div className="space-y-2">
                    {healer.insurance_accepted.map((ins, idx) => (
                      <p key={idx} className="text-gray-700">{ins}</p>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Reviews Section */}
            {reviews.length > 0 && (
              <Card className="p-8" data-testid="reviews-section">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Reviews ({reviews.length})</h2>
                  {user && reviewableBookings.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setShowReviewForm(true)}
                      data-testid="write-review-btn"
                    >
                      Write a Review
                    </Button>
                  )}
                </div>
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0" data-testid={`review-${review.id}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center text-white font-bold text-sm">
                          {review.patient_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{review.patient_name}</p>
                          <p className="text-xs text-gray-500">{format(new Date(review.created_at), 'PPP')}</p>
                        </div>
                        <div className="flex ml-auto">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </div>
                      {review.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {review.tags.map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs py-0.5 px-2 bg-amber-50 border-amber-200 text-amber-700">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-gray-700 text-sm leading-relaxed">{review.text}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Write Review prompt when no reviews exist */}
            {reviews.length === 0 && user && reviewableBookings.length > 0 && (
              <Card className="p-8 text-center" data-testid="first-review-prompt">
                <Star className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-2">Be the first to review!</h3>
                <p className="text-gray-600 text-sm mb-4">Share your experience with this healer</p>
                <Button onClick={() => setShowReviewForm(true)} className="bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="first-review-btn">
                  Write a Review
                </Button>
              </Card>
            )}
          </div>

          {/* Contact Section */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24" data-testid="contact-section">
              <h2 className="text-2xl font-bold mb-6">Contact This Healer</h2>

              <div className="space-y-4 mb-6">
                {healer.contact_email && (
                  <a href={`mailto:${healer.contact_email}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-amber-50 transition-colors group" data-testid="contact-email">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-[#8B4513]">{healer.contact_email}</p>
                    </div>
                  </a>
                )}

                {healer.contact_phone && (
                  <a href={`tel:${healer.contact_phone}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-amber-50 transition-colors group" data-testid="contact-phone">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-[#8B4513]">{healer.contact_phone}</p>
                    </div>
                  </a>
                )}

                {healer.contact_website && (
                  <a href={healer.contact_website.startsWith('http') ? healer.contact_website : `https://${healer.contact_website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg hover:bg-amber-50 transition-colors group" data-testid="contact-website">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" /></svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Website</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-[#8B4513]">{healer.contact_website}</p>
                    </div>
                  </a>
                )}

                {healer.contact_instagram && (
                  <a href={`https://instagram.com/${healer.contact_instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg hover:bg-amber-50 transition-colors group" data-testid="contact-instagram">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-700" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Instagram</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-[#8B4513]">{healer.contact_instagram}</p>
                    </div>
                  </a>
                )}

                {healer.blog_url && (
                  <a href={healer.blog_url.startsWith('http') ? healer.blog_url : `https://${healer.blog_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg hover:bg-amber-50 transition-colors group" data-testid="contact-blog">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Blog</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-[#8B4513]">Read my blog</p>
                    </div>
                  </a>
                )}
              </div>

              {/* Send Message */}
              {!showMessageForm ? (
                <Button
                  className="w-full bg-[#8B4513] hover:bg-[#6B3410] text-white"
                  onClick={() => setShowMessageForm(true)}
                  data-testid="send-message-btn"
                >
                  Send a Message
                </Button>
              ) : (
                <div className="space-y-3" data-testid="message-form">
                  <Input
                    placeholder="Your name"
                    value={messageName}
                    onChange={(e) => setMessageName(e.target.value)}
                    data-testid="message-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={messageEmail}
                    onChange={(e) => setMessageEmail(e.target.value)}
                    data-testid="message-email-input"
                  />
                  <Textarea
                    placeholder="Write your message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={4}
                    data-testid="message-text-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-[#8B4513] hover:bg-[#6B3410] text-white"
                      onClick={handleSendMessage}
                      disabled={sendingMessage}
                      data-testid="submit-message-btn"
                    >
                      {sendingMessage ? 'Sending...' : 'Send'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowMessageForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Rate & Experience */}
              <div className="mt-6 pt-6 border-t space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Hourly Rate</span>
                  <span className="font-semibold" style={{color: '#8B4513'}}>${healer.hourly_rate}/hr</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Experience</span>
                  <span className="font-semibold">{healer.years_experience} years</span>
                </div>
                {healer.ethnicity && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Background</span>
                    <span className="font-semibold">{healer.ethnicity}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewForm} onOpenChange={setShowReviewForm}>
        <DialogContent className="max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Write a Review</DialogTitle>
            <DialogDescription>Share your experience with {healer.user?.name}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Select Booking */}
            <div>
              <Label className="mb-2 block">Select Booking</Label>
              <select
                value={reviewBookingId}
                onChange={(e) => setReviewBookingId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                data-testid="review-booking-select"
              >
                <option value="">Choose a booking...</option>
                {reviewableBookings.map(b => (
                  <option key={b.id} value={b.id}>
                    {format(new Date(b.date), 'PPP')} — {b.start_time}-{b.end_time}
                  </option>
                ))}
              </select>
            </div>

            {/* Star Rating */}
            <div>
              <Label className="mb-2 block">Rating</Label>
              <div className="flex gap-1" data-testid="review-stars">
                {[1,2,3,4,5].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setReviewRating(s)}
                    className="focus:outline-none"
                    data-testid={`star-${s}`}
                  >
                    <Star className={`w-8 h-8 transition-colors ${s <= reviewRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 hover:text-amber-200'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <Label className="mb-2 block">Tags (optional)</Label>
              <div className="flex flex-wrap gap-2" data-testid="review-tags">
                {REVIEW_TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                      reviewTags.includes(tag)
                        ? 'bg-amber-100 border-amber-400 text-amber-800 font-semibold'
                        : 'border-gray-300 text-gray-600 hover:border-amber-300'
                    }`}
                    data-testid={`tag-${tag.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Review Text */}
            <div>
              <Label htmlFor="review-text" className="mb-2 block">Your Review</Label>
              <Textarea
                id="review-text"
                placeholder="Tell others about your experience..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows={4}
                data-testid="review-text-input"
              />
            </div>
          </div>

          <Button
            onClick={handleSubmitReview}
            disabled={submittingReview}
            className="w-full bg-[#8B4513] hover:bg-[#6B3410] text-white"
            data-testid="submit-review-btn"
          >
            {submittingReview ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Gallery Lightbox */}
      {lightboxIndex !== null && galleryPhotos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="lightbox-close"
            aria-label="Close gallery"
          >
            <X className="w-6 h-6" />
          </button>
          {galleryPhotos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
                className="absolute left-2 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="lightbox-prev"
                aria-label="Previous photo"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
                className="absolute right-2 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="lightbox-next"
                aria-label="Next photo"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img
            src={galleryUrl(galleryPhotos[lightboxIndex])}
            alt={`Gallery ${lightboxIndex + 1}`}
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-sm">
            {lightboxIndex + 1} / {galleryPhotos.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default HealerProfile;